import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SupabaseClient } from '../supabase/client';
import type {
  CheckoutFulfillmentQuoteInput,
  FulfillmentOutcome,
  FulfillmentQuote,
  PreparedFulfillmentQuoteInput,
  PublicFulfillmentOptions,
  SaveStoreFulfillmentInput,
  StoreFulfillmentSettings,
} from '../types/store-fulfillment';
import {
  getPublicFulfillmentOptions,
  getStoreFulfillmentSettings,
  quoteCheckoutFulfillment,
  quotePreparedFulfillment,
  saveStoreFulfillmentSettings,
} from '../services/store-fulfillment.service';
import { customerQueryKeys } from './customer-query-keys';

export const fulfillmentQueryKeys = {
  settings: (storeId: string | null) => ['fulfillment', 'settings', storeId] as const,
  options: (storeId: string | null) => ['fulfillment', 'options', storeId] as const,
  checkoutQuote: (
    userId: string | null,
    input: (CheckoutFulfillmentQuoteInput & { readonly cartFingerprint: string }) | null,
  ) =>
    [
      ...customerQueryKeys.all,
      'fulfillment',
      'checkout-quote',
      userId,
      input?.storeId ?? null,
      input?.selection ?? null,
      input?.cartFingerprint ?? null,
    ] as const,
  preparedQuote: (userId: string | null, input: PreparedFulfillmentQuoteInput | null) =>
    [
      'fulfillment',
      'prepared-quote',
      userId,
      input?.storeId ?? null,
      input?.selection ?? null,
      input ? [...input.lines].sort((a, b) => a.variant_id.localeCompare(b.variant_id)) : null,
    ] as const,
};

export async function invalidateFulfillmentQuotes(
  queryClient: QueryClient,
  storeId: string,
): Promise<void> {
  await queryClient.invalidateQueries({
    predicate: ({ queryKey: key }) =>
      (key[0] === 'fulfillment' && key[1] === 'prepared-quote' && key[3] === storeId) ||
      (key[0] === customerQueryKeys.all[0] &&
        key[1] === 'fulfillment' &&
        key[2] === 'checkout-quote' &&
        key[4] === storeId),
    refetchType: 'none',
  });
}

export function useStoreFulfillmentSettings(
  client: SupabaseClient,
  storeId: string | null,
): UseQueryResult<StoreFulfillmentSettings> {
  return useQuery({
    queryKey: fulfillmentQueryKeys.settings(storeId),
    enabled: Boolean(storeId),
    retry: false,
    queryFn: () => {
      if (!storeId) throw new Error('Store required');
      return getStoreFulfillmentSettings(client, storeId);
    },
  });
}
export function useSaveStoreFulfillmentSettings(
  client: SupabaseClient,
): UseMutationResult<
  FulfillmentOutcome<StoreFulfillmentSettings>,
  unknown,
  SaveStoreFulfillmentInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: (input: SaveStoreFulfillmentInput) => saveStoreFulfillmentSettings(client, input),
    onSuccess: async (result, input) => {
      if (result.success || result.error_code === 'FULFILLMENT_CONFIG_CONFLICT') {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: fulfillmentQueryKeys.settings(input.storeId) }),
          queryClient.invalidateQueries({ queryKey: fulfillmentQueryKeys.options(input.storeId) }),
          invalidateFulfillmentQuotes(queryClient, input.storeId),
        ]);
      }
    },
  });
}
export function usePublicFulfillmentOptions(
  client: SupabaseClient,
  storeId: string | null,
  enabled = true,
): UseQueryResult<PublicFulfillmentOptions | null> {
  return useQuery({
    queryKey: fulfillmentQueryKeys.options(storeId),
    enabled: enabled && Boolean(storeId),
    retry: false,
    staleTime: 0,
    queryFn: () => {
      if (!storeId) throw new Error('Store required');
      return getPublicFulfillmentOptions(client, storeId);
    },
  });
}
export function useCheckoutFulfillmentQuote(
  client: SupabaseClient,
  userId: string | null,
  input: (CheckoutFulfillmentQuoteInput & { readonly cartFingerprint: string }) | null,
  enabled = true,
): UseQueryResult<FulfillmentOutcome<FulfillmentQuote>> {
  return useQuery({
    queryKey: fulfillmentQueryKeys.checkoutQuote(userId, input),
    enabled: enabled && Boolean(userId && input?.storeId && input.cartFingerprint),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: ({ signal }) => {
      if (!userId || !input) throw new Error('Customer and selection required');
      return quoteCheckoutFulfillment(client, input, signal);
    },
  });
}
export function usePreparedFulfillmentQuote(
  client: SupabaseClient,
  userId: string | null,
  input: PreparedFulfillmentQuoteInput | null,
): UseQueryResult<FulfillmentOutcome<FulfillmentQuote>> {
  return useQuery({
    queryKey: fulfillmentQueryKeys.preparedQuote(userId, input),
    enabled: Boolean(userId && input?.storeId && input.lines.length),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: ({ signal }) => {
      if (!userId || !input) throw new Error('Member and selection required');
      return quotePreparedFulfillment(client, input, signal);
    },
  });
}
